import type { Order, OrderStatus } from '@/types/domain';
import { Card } from '@/components/atoms/Card';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms';
import styles from './OrderCard.module.css';
import dayjs from 'dayjs';

// F-190 QA B7: status badge in chat-bubble proposal cards. The bubble was
// rendering title + price only, so a client who just submitted "created"
// orders had no in-chat confirmation that the order was now waiting for
// the master. Keep the mapping in sync with OrderCard's local table.
const STATUS_BADGE: Record<OrderStatus, { text: string; variant: 'default' | 'accent' | 'success' | 'warning' | 'danger' }> = {
  created: { text: 'Очікує підтвердження', variant: 'warning' },
  proposed: { text: 'Очікує підтвердження', variant: 'warning' },
  accepted: { text: 'Прийнято', variant: 'accent' },
  in_progress: { text: 'В роботі', variant: 'accent' },
  completed: { text: 'Виконано', variant: 'success' },
  reviewed: { text: 'З відгуком', variant: 'success' },
  cancelled: { text: 'Скасовано', variant: 'danger' },
};

export interface OrderProposalCardProps {
  order: Order;
  canAccept?: boolean;
  canDecline?: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
}

export function OrderProposalCard({
  order,
  canAccept,
  canDecline,
  onAccept,
  onDecline,
}: OrderProposalCardProps): JSX.Element {
  return (
    <Card>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.title}>{order.title}</span>
          <span className={styles.price}>{Number(order.price_eur).toFixed(2)} €</span>
        </div>
        {order.description ? <div className={styles.meta}>{order.description}</div> : null}
        {order.scheduled_at ? (
          <div className={styles.meta}>
            <Icon name="calendar" size={14} />
            <span>
              {dayjs(order.scheduled_at).format('D MMM, HH:mm')}
              {order.duration_minutes ? ` · ${order.duration_minutes} хв` : ''}
            </span>
          </div>
        ) : null}
        {(() => {
          const badge = STATUS_BADGE[order.status];
          const label = order.status_label || badge?.text;
          if (!label) return null;
          return (
            <div className={styles.statusRow}>
              <Badge variant={badge?.variant ?? 'default'}>{label}</Badge>
            </div>
          );
        })()}
        {(canAccept || canDecline) && (
          <div className={styles.actions}>
            {canAccept ? <Button onClick={onAccept}>Прийняти</Button> : null}
            {canDecline ? (
              <Button variant="secondary" onClick={onDecline}>
                Відхилити
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
